/* The mail about this document, on the document.
 *
 * An invoice is not just a number: it is the mail you sent with it, the
 * reply asking for a PO number, and the chaser three weeks later. ERPNext
 * keeps all of that against the document and then shows it on a desk most
 * tenants never open, so the person chasing payment is in their inbox
 * guessing.
 */
import { useCallback, useEffect, useState } from 'react'
import type { ErpError } from './api'
import { useErp } from './context'
import { formatDatetime } from './format'
import { sanitiseHtml } from './sanitise'
import type { EmailThread } from './types'

export function Emails({ doctype, name, suggestTo, suggestSubject }: {
  doctype: string
  name: string
  /** Who the document is about, so a reply does not start empty. */
  suggestTo?: string
  suggestSubject?: string
}) {
  const erp = useErp()
  const [data, setData] = useState<EmailThread | null>(null)
  const [writing, setWriting] = useState(false)
  const [to, setTo] = useState(suggestTo ?? '')
  const [subject, setSubject] = useState(suggestSubject ?? `${doctype} ${name}`)
  const [body, setBody] = useState('')
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)

  const load = useCallback(() => {
    erp.emails(doctype, name).then(setData).catch(() => setData(null))
  }, [erp, doctype, name])
  useEffect(load, [load])

  if (!data) return null
  if (!data.emails.length && !data.can_send) return null

  const send = async () => {
    setBusy(true); setFailed(null)
    try {
      await erp.sendEmail(doctype, name, { to, subject, body })
      setWriting(false); setBody('')
      load()
    } catch (e) { setFailed((e as ErpError).message) }
    finally { setBusy(false) }
  }

  return (
    <section className="card card-pad stack" style={{ gap: 12 }} data-part="emails">
      <div className="row-between">
        <strong className="small">
          Email {data.emails.length ? `· ${data.emails.length}` : ''}
        </strong>
        {data.can_send ? (
          <button className="btn btn-sm" onClick={() => setWriting((v) => !v)}>
            {writing ? 'Not now' : data.emails.length ? 'Reply' : 'Send one'}
          </button>
        ) : (
          <span className="small muted">
            No email account set up yet, so nothing can be sent from here.
          </span>
        )}
      </div>

      {data.emails.map((m) => (
        <article key={m.id} className="mail" data-out={m.outgoing || undefined}>
          <div className="row-between">
            <span className="small" style={{ fontWeight: 560 }}>
              {m.outgoing ? `To ${m.to.join(', ') || 'somebody'}` : `From ${m.from_name || m.from}`}
            </span>
            <span className="small muted">{formatDatetime(m.when)}</span>
          </div>
          {m.subject ? <div className="small muted">{m.subject}</div> : null}
          {/* Sanitised: this is somebody else's HTML, arriving from outside. */}
          <div className="mail-body"
               dangerouslySetInnerHTML={{ __html: sanitiseHtml(m.body) }} />
          {m.outgoing ? (
            <div className="small muted">
              {m.seen ? 'Opened by them' : m.status === 'Sent' ? 'Sent' : m.status}
            </div>
          ) : null}
        </article>
      ))}

      {writing ? (
        <div className="stack" style={{ gap: 8 }}>
          <label className="field">
            <span style={{ fontSize: 12.5, fontWeight: 550, color: 'var(--ink-2)' }}>To</span>
            <input className="input" value={to} onChange={(e) => setTo(e.target.value)}
                   placeholder="accounts@customer.test" />
          </label>
          <label className="field">
            <span style={{ fontSize: 12.5, fontWeight: 550, color: 'var(--ink-2)' }}>About</span>
            <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </label>
          <label className="field">
            <span style={{ fontSize: 12.5, fontWeight: 550, color: 'var(--ink-2)' }}>Message</span>
            <textarea className="input" rows={5} value={body}
                      onChange={(e) => setBody(e.target.value)} />
          </label>
          {failed ? <div className="err" role="alert">{failed}</div> : null}
          <div className="row">
            <span className="small muted">
              It is kept against this {doctype.toLowerCase()}, so the next person sees it too.
            </span>
            <span className="grow" />
            <button className="btn btn-primary" disabled={busy || !to.trim() || !body.trim()}
                    onClick={() => void send()}>
              {busy ? 'Sending…' : 'Send'}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  )
}
